/**
 * Run Ace fresh on the same task — same or historical criteria — and compare trials.
 */

import { useEffect, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlask, faStar } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import {
  agentReviewAPI,
  type AceProfileInfo,
  type AceTrialSummary,
  type CriteriaVersionRecord,
} from "@/services/api/agentReview";
import { AceTrialDiffPanel } from "./AceTrialDiffPanel";
import { aceTrialOptionLabel, aceTrialRagPair, aceTrialRunBadge, aceTrialTitle } from "./aceTrialLabels";
import type { AceProfile, Rag, ReviewComparison } from "./types";

interface AceFreshRunPanelProps {
  reviewId: string;
  catalogueKey: string | null;
  loading: boolean;
  onRunFresh: (args: {
    criteriaVersionId: string | null;
    aceProfile: AceProfile;
    note: string;
    promote: boolean;
  }) => void;
  onSelectTrial: (trialId: string) => void;
  onPromoteTrial: (trialId: string) => void;
  activeTrialId?: string | null;
  comparison?: ReviewComparison | null;
  refreshToken?: number;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ragPair(orch: Rag | null, out: Rag | null): string {
  return aceTrialRagPair(orch, out);
}

export function AceFreshRunPanel({
  reviewId,
  catalogueKey,
  loading,
  onRunFresh,
  onSelectTrial,
  onPromoteTrial,
  activeTrialId = null,
  comparison = null,
  refreshToken = 0,
}: AceFreshRunPanelProps) {
  const [criteriaVersions, setCriteriaVersions] = useState<CriteriaVersionRecord[]>([]);
  const [aceProfiles, setAceProfiles] = useState<AceProfileInfo[]>([]);
  const [trials, setTrials] = useState<AceTrialSummary[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState<string>("");
  const [selectedProfile, setSelectedProfile] = useState<AceProfile>("strict");
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    setMetaLoading(true);
    void (async () => {
      try {
        const [trialRows, criteriaRows, profileRows] = await Promise.all([
          agentReviewAPI.listAceTrials(reviewId),
          catalogueKey ? agentReviewAPI.listCriteriaVersions(catalogueKey) : Promise.resolve([]),
          agentReviewAPI.listAceProfiles(),
        ]);
        if (cancelled) return;
        setTrials(trialRows);
        setCriteriaVersions(criteriaRows);
        setAceProfiles(profileRows);
        if (!selectedCriteriaId && criteriaRows.length > 0) {
          const current = criteriaRows.find((v) => v.is_current) ?? criteriaRows[0];
          setSelectedCriteriaId(current.criteria_version_id);
        }
        if (profileRows.length > 0 && !profileRows.some((p) => p.id === selectedProfile)) {
          const defaultProfile = profileRows.find((p) => p.is_default) ?? profileRows[0];
          setSelectedProfile(defaultProfile.id);
        }
      } catch {
        if (!cancelled) {
          setTrials([]);
        }
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewId, catalogueKey, refreshToken]);

  const selectedCriteria = criteriaVersions.find((v) => v.criteria_version_id === selectedCriteriaId);
  const selectedProfileInfo = aceProfiles.find((p) => p.id === selectedProfile);

  return (
    <div className="rounded-xl bg-white/[0.02] px-4 py-4 ring-1 ring-white/10 space-y-4">
      <div className="flex items-start gap-3">
        <FontAwesomeIcon icon={faFlask} className="text-indigo-300 mt-0.5" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium text-slate-200">Fresh Ace run</p>
          <p className="text-xs text-slate-500 leading-relaxed">
            Re-run Ace on this same task with current or historical criteria, or try a less strict
            grading profile to compare against the original strict review.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide">
          Grading profile
        </label>
        {metaLoading && aceProfiles.length === 0 ? (
          <LoadingPlaceholder text="Loading profiles…" />
        ) : (
          <select
            value={selectedProfile}
            onChange={(e) => setSelectedProfile(e.target.value as AceProfile)}
            className="w-full rounded-lg bg-slate-900/60 px-3 py-2 text-sm text-slate-200 ring-1 ring-white/10 focus:outline-none focus:ring-indigo-500/40"
          >
            {(aceProfiles.length > 0
              ? aceProfiles
              : [
                  { id: "strict" as const, label: "Strict", description: "", is_default: true },
                  { id: "balanced" as const, label: "Balanced", description: "", is_default: false },
                  { id: "lenient" as const, label: `Lenient`, description: "", is_default: false },
                ]
            ).map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.label}
                {profile.is_default ? " (original)" : ""}
              </option>
            ))}
          </select>
        )}
        {selectedProfileInfo?.description ? (
          <p className="text-[11px] text-slate-400 leading-relaxed">{selectedProfileInfo.description}</p>
        ) : null}
        {selectedProfile !== "strict" ? (
          <p className="text-[11px] text-amber-300/90">
            Non-strict profile — compare this trial against the primary strict run to see where Ace
            relaxes.
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide">
          Criteria version
        </label>
        {metaLoading && criteriaVersions.length === 0 ? (
          <LoadingPlaceholder text="Loading criteria…" />
        ) : (
          <select
            value={selectedCriteriaId}
            onChange={(e) => setSelectedCriteriaId(e.target.value)}
            className="w-full rounded-lg bg-slate-900/60 px-3 py-2 text-sm text-slate-200 ring-1 ring-white/10 focus:outline-none focus:ring-indigo-500/40"
          >
            {criteriaVersions.length === 0 ? (
              <option value="">Current live criteria</option>
            ) : (
              criteriaVersions.map((version) => (
                <option key={version.criteria_version_id} value={version.criteria_version_id}>
                  {version.label}
                  {version.is_current ? " (current)" : ""}
                </option>
              ))
            )}
          </select>
        )}
        {selectedCriteria && !selectedCriteria.is_current ? (
          <p className="text-[11px] text-amber-300/90">
            Historical criteria — compare how Ace would rate under the old checklist.
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide">
          Note (optional)
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Consistency check after prompt tweak"
          className="w-full rounded-lg bg-slate-900/60 px-3 py-2 text-sm text-slate-200 ring-1 ring-white/10 placeholder:text-slate-600 focus:outline-none focus:ring-indigo-500/40"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          color="indigo"
          className="!text-xs"
          disabled={loading}
          onClick={() =>
            onRunFresh({
              criteriaVersionId: selectedCriteriaId || null,
              aceProfile: selectedProfile,
              note: note.trim(),
              promote: false,
            })
          }
        >
          {loading ? "Ace is running…" : "Run fresh Ace (keep trial)"}
        </Button>
        <Button
          color="slate"
          className="!text-xs"
          disabled={loading}
          onClick={() =>
            onRunFresh({
              criteriaVersionId: selectedCriteriaId || null,
              aceProfile: selectedProfile,
              note: note.trim() || "Primary Ace rating",
              promote: true,
            })
          }
        >
          Run and set as primary
        </Button>
      </div>

      {comparison && !comparison.is_canonical && comparison.trial_id ? (
        <p className="text-xs text-slate-400">
          Viewing {comparison.ace_run_number ? aceTrialRunBadge(comparison.ace_run_number) : "trial"}
          {comparison.criteria_version_label ? ` (${comparison.criteria_version_label})` : ""}
          {" — "}
          not the primary Ace rating.
        </p>
      ) : null}

      {metaLoading && trials.length === 0 ? (
        <LoadingPlaceholder text="Loading Ace trials…" />
      ) : trials.length > 0 ? (
        <div className="space-y-3 border-t border-white/5 pt-3">
          <AceTrialDiffPanel
            reviewId={reviewId}
            trials={trials}
            activeTrialId={activeTrialId}
            refreshToken={refreshToken}
            onViewTrial={onSelectTrial}
          />

          <div className="space-y-2">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
              Ace trial history ({trials.length})
            </p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {trials.map((trial) => {
              const active = activeTrialId === trial.trial_id;
              return (
                <div
                  key={trial.trial_id}
                  className={clsx(
                    "rounded-lg px-3 py-2 ring-1 flex flex-wrap items-center gap-2",
                    active
                      ? "bg-indigo-500/[0.08] ring-indigo-500/25"
                      : "bg-white/[0.02] ring-white/5",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelectTrial(trial.trial_id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      {trial.run_number ? (
                        <span className="shrink-0 rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-medium text-slate-300 ring-1 ring-slate-500/20 tabular-nums">
                          #{trial.run_number}
                        </span>
                      ) : null}
                      <p className="text-xs text-slate-200 truncate">{aceTrialTitle(trial)}</p>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {formatWhen(trial.created_at)} · {ragPair(trial.orch_rag, trial.out_rag)}
                    </p>
                  </button>
                  {trial.is_canonical ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-200 ring-1 ring-emerald-500/20">
                      <FontAwesomeIcon icon={faStar} className="text-[9px]" />
                      Primary
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onPromoteTrial(trial.trial_id)}
                      className="text-[11px] text-indigo-300 hover:text-indigo-200"
                    >
                      Set primary
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
